import {
	Group,
	Skeleton,
	Stack,
	Table,
	ThemeIcon,
	UnstyledButton,
	useMantineTheme,
} from '@mantine/core';
import {
	fetchListWorkspacePrompts,
	listWorkspacePromptsQueryKey,
	type EmbeddedPromptOut,
} from '@repo/api-codegen';
import { Icon, Text } from '@repo/foundations';
import { useInfiniteQuery } from '@tanstack/react-query';
import { flatMapDeep, isNil } from 'lodash-es';
import { memo, useCallback, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { sanitizePromptMessage } from '../../../../components/AIAssistant/utils';
import { UserAvatar } from '../../../../components/UserAvatar';
import { AiChatPreviewModalController } from '../../../../features/AiChatPreviewModal/AiChatPreviewModalController';
import { AiQualityBadgeController } from '../../../../features/AiQualityBadge';
import { formatShortTimeDifference } from '../../../../utils/time';

function TableHead() {
	return (
		<tr style={{ backgroundColor: 'white' }}>
			<th>
				<Text color="text/secondary/default" fz="xs" fw="semibold">
					Initial question
				</Text>
			</th>
			<th style={{ width: '80px' }}>
				<Text color="text/secondary/default" fz="xs" fw="semibold">
					Score
				</Text>
			</th>
		</tr>
	);
}

export const MetricAIQueryTableAll = memo(() => {
	const theme = useMantineTheme();
	const [promptId, setPromptId] = useState<string | null>(null);
	const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
		queryKey: listWorkspacePromptsQueryKey({}),
		queryFn: ({ signal, pageParam }) =>
			fetchListWorkspacePrompts(
				{ queryParams: { page: pageParam.page } },
				signal
			),
		initialPageParam: { page: 1 },
		getPreviousPageParam: (firstPage) =>
			isNil(firstPage.meta.previous_page)
				? null
				: { page: firstPage.meta.previous_page },
		getNextPageParam: (lastPage) =>
			isNil(lastPage.meta.next_page) ? null : { page: lastPage.meta.next_page },
		select: ({ pages, pageParams }) => ({
			prompts: flatMapDeep(pages, (page) => page.results),
			pageParams,
			totalCount: pages?.[0]?.count ?? 0,
		}),
	});

	const loadMore = useCallback(() => {
		if (hasNextPage) {
			fetchNextPage();
		}
	}, [fetchNextPage, hasNextPage]);

	const itemContent = useCallback(
		(_: number, prompt: EmbeddedPromptOut) => (
			<>
				<td>
					<UnstyledButton onClick={() => setPromptId(prompt.id)} w="100%">
						<Group h={52} spacing="sm" noWrap>
							{prompt.user ? (
								<UserAvatar
									user={{
										id: prompt.user.id,
										email: prompt.user.email,
										display_name: prompt.user.display_name,
										first_name: prompt.user.first_name,
										last_name: prompt.user.last_name,
										disabled: prompt.user.disabled ?? false,
										profile_picture: prompt.user.profile_picture,
									}}
									size={28}
								/>
							) : (
								<ThemeIcon
									color={theme.other.getColor('fill/secondary/default')}
									size={28}
									radius={28}
								>
									<Icon name="sparkles" color="icon/primary/default" />
								</ThemeIcon>
							)}
							<Stack spacing={0}>
								<Text
									lineClamp={1}
									color="text/primary/default"
									size="sm"
									weight="semibold"
								>
									{sanitizePromptMessage(String(prompt.prompt || ''))}
								</Text>
								<Text color="text/secondary/default" size="xs">
									{prompt.user && `Asked by ${prompt.user?.display_name} · `}{' '}
									{formatShortTimeDifference(prompt.created_at)}
								</Text>
							</Stack>
						</Group>
					</UnstyledButton>
				</td>
				<td>
					{!isNil(prompt.benchmark_raw?.score) && (
						<UnstyledButton onClick={() => setPromptId(prompt.id)} w="100%">
							<AiQualityBadgeController score={prompt.benchmark_raw?.score} />
						</UnstyledButton>
					)}
				</td>
			</>
		),
		[theme]
	);

	if (isLoading) {
		return (
			<Stack>
				<Skeleton height={52} />
				<Skeleton height={52} />
			</Stack>
		);
	}

	return (
		<>
			{promptId && (
				<AiChatPreviewModalController
					promptId={promptId}
					onClose={() => setPromptId(null)}
				/>
			)}
			<TableVirtuoso<EmbeddedPromptOut>
				style={{ width: '100%', height: '100%' }}
				components={{
					Table,
				}}
				fixedHeaderContent={TableHead}
				data={data?.prompts ?? []}
				totalCount={data?.totalCount ?? 0}
				itemContent={itemContent}
				endReached={loadMore}
				increaseViewportBy={1000}
			/>
		</>
	);
});
